import Icon from 'components/_UI/Icon';
import { useEffect, useState } from 'react';
import styled from 'styled-components'
import { Column } from 'styled/alignment/Column';
import breakpoints from 'theme/breakpoints';
import colors from 'theme/colors';
import DashHeader from './DashHeader';
import DashHeaderActions from './DashHeaderActions';
import QuickActionsButton from './QuickActionsButton';
import paths from 'routes/paths';
import { Link, useLocation } from 'react-router-dom';
import Text from 'components/_UI/Text';
import { useUser } from 'context/user/User';
import { useOnSelectMenu } from 'context/general/OnSelectMenu';
import { useReservationNotificationsOptional } from 'context/reservations/ReservationNotifications';
import roles from 'helpers/roles';
import { ESTABLISHMENT_ROLES, PRODUCER_ROLES } from 'helpers/dashboardAccess';
import { BarChart2, Calendar, CalendarCheck, Package, DollarSign, ShoppingBag, Monitor, CheckSquare, HelpCircle, User, Clipboard, Clock, UtensilsCrossed, ChefHat, ClipboardList, Wallet, Settings, Users, UserPlus, Tag, Megaphone, ScanSearch, ChevronLeft, ChevronRight } from 'lucide-react';
import DynamicProfileImage from 'components/commons/DynamicProfileImage';
import usePanelPermissions from 'hooks/usePanelPermissions';
import PanelPermissionToast from 'modules/dashboard/producer-access/PanelPermissionToast';

const featherIcons = {
    'chart': BarChart2,
    'cheers': Calendar,
    'label': Package,
    'money': DollarSign,
    'orders': ShoppingBag,
    'twoPageStore': Monitor,
    'qrcode': CheckSquare,
    'support': HelpCircle,
    'profile': User,
    'task': Clipboard,
    // Establishment sidebar — kept in sync with DashEstablishmentSlot.js's navIcons
    'menu': UtensilsCrossed,
    'kitchen': ChefHat,
    'waiter': ClipboardList,
    'cashier': Wallet,
    'admin': Settings,
    'statistics': BarChart2,
    'customers': Users,
    'promotions': Tag,
    'inventory': Package,
    'posEvents': Calendar,
    'reservations': CalendarCheck,
    'campaigns': Megaphone,
    'teams': UserPlus,
    'instagram': ScanSearch,
};

/* "Novo" some assim que o produtor abre a tela pela primeira vez — guardado
   por navegador (localStorage), não por conta, então é só um lembrete visual
   local, não uma preferência de verdade. */
const NEW_FEATURE_ROUTES = {
    [paths.dashboard.teams]: 'whizr_seen_feature_teams',
    [paths.dashboard.campaigns]: 'whizr_seen_feature_campaigns',
    [paths.dashboard.instagram]: 'whizr_seen_feature_instagram',
};

function hasSeenFeature(storageKey) {
    try {
        return localStorage.getItem(storageKey) === '1';
    } catch (error) {
        return false;
    }
}

function markFeatureSeen(storageKey) {
    try {
        localStorage.setItem(storageKey, '1');
    } catch (error) {
        // localStorage indisponível (modo privado, storage cheio etc.) — o
        // badge só fica reaparecendo, não quebra a navegação.
    }
}

export default function DashSlot({ children }) {
    const { user } = useUser();
    const { isLateralMenuActive, setIsLateralMenuActive, isSidebarCollapsed, toggleSidebarCollapsed } = useOnSelectMenu();
    const reservationNotifications = useReservationNotificationsOptional();
    const pendingReservationsCount = reservationNotifications?.pendingCount || 0;
    const location = useLocation();
    const { isActing, canOpen } = usePanelPermissions();

    const [seenFeatures, setSeenFeatures] = useState(() => ({
        [paths.dashboard.teams]: hasSeenFeature(NEW_FEATURE_ROUTES[paths.dashboard.teams]),
        [paths.dashboard.campaigns]: hasSeenFeature(NEW_FEATURE_ROUTES[paths.dashboard.campaigns]),
        [paths.dashboard.instagram]: hasSeenFeature(NEW_FEATURE_ROUTES[paths.dashboard.instagram]),
    }));

    useEffect(() => {
        const storageKey = NEW_FEATURE_ROUTES[location.pathname];

        if (storageKey && !seenFeatures[location.pathname]) {
            markFeatureSeen(storageKey);
            setSeenFeatures(prev => ({ ...prev, [location.pathname]: true }));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.pathname]);

    const inEstablishmentPanel = location.pathname.startsWith(paths.dashboard.establishment.root);

    const mainNavItems = [
        { name: 'Dashboard', icon: 'chart', route: paths.dashboard.statistics },
        { name: 'Eventos', icon: 'cheers', route: paths.dashboard.events },
        { name: 'Produtos', icon: 'label', route: paths.dashboard.products },
        { name: 'Clientes', icon: 'customers', route: paths.dashboard.customers, roles: PRODUCER_ROLES },
        { name: 'Campanhas', icon: 'campaigns', route: paths.dashboard.campaigns, roles: PRODUCER_ROLES, isNew: !seenFeatures[paths.dashboard.campaigns] },
        { name: 'Instagram', icon: 'instagram', route: paths.dashboard.instagram, roles: PRODUCER_ROLES, isNew: !seenFeatures[paths.dashboard.instagram] },
        { name: 'Perfil', icon: 'profile', route: paths.dashboard.profile, roles: PRODUCER_ROLES },
    ];

    const managementNavItems = [
        { name: 'Financeiro', icon: 'money', route: paths.dashboard.financial },
        { name: 'Pedidos', icon: 'orders', route: paths.dashboard.orders },
        { name: 'Equipes', icon: 'teams', route: paths.dashboard.teams, roles: PRODUCER_ROLES, isNew: !seenFeatures[paths.dashboard.teams] },
        // PDV desativado por enquanto (aparecia pra todo produtor sem checar
        // se ele de fato é um estabelecimento com ponto de venda físico).
        // { name: 'PDV', icon: 'twoPageStore', route: paths.dashboard.orders_establishment },
    ];

    const toolNavItems = [
        { name: 'Checkin', icon: 'qrcode', route: paths.dashboard.checkin },
        { name: 'Ajuda', icon: 'support', route: paths.dashboard.support },
    ];

    const comingSoonItems = [
        { name: 'Tarefas', icon: 'task', roles: [roles.PRODUCER, roles.ESTABLISHMENT, roles.ADMIN] },
    ];

    const establishmentSections = [
        {
            label: 'Operação',
            items: [
                { name: 'Perfil',        icon: 'profile',    route: paths.dashboard.establishment.profile,    roles: ESTABLISHMENT_ROLES },
                { name: 'Cardápio',      icon: 'menu',       route: paths.dashboard.establishment.menu,       roles: ESTABLISHMENT_ROLES },
                { name: 'Pedidos',       icon: 'kitchen',    route: paths.dashboard.establishment.kitchen,    roles: ESTABLISHMENT_ROLES },
                { name: 'Reservas',      icon: 'reservations', route: paths.dashboard.establishment.reservations, roles: ESTABLISHMENT_ROLES },
                { name: 'Comandas',      icon: 'waiter',     route: paths.dashboard.establishment.waiter,     roles: ESTABLISHMENT_ROLES },
                { name: 'Caixa',         icon: 'cashier',    route: paths.dashboard.establishment.cashier,    roles: ESTABLISHMENT_ROLES },
                { name: 'Eventos',       icon: 'posEvents',  route: paths.dashboard.establishment.posEvents,  roles: ESTABLISHMENT_ROLES },
            ],
        },
        {
            label: 'Gestão',
            items: [
                { name: 'Estoque',       icon: 'inventory',  route: paths.dashboard.establishment.inventory,  roles: ESTABLISHMENT_ROLES, isNew: true },
                { name: 'Promoções',    icon: 'promotions', route: paths.dashboard.establishment.promotions, roles: ESTABLISHMENT_ROLES },
                { name: 'Administração', icon: 'admin',     route: paths.dashboard.establishment.admin,      roles: ESTABLISHMENT_ROLES },
            ],
        },
        {
            label: 'Análise',
            items: [
                { name: 'Estatísticas', icon: 'statistics', route: paths.dashboard.establishment.statistics, roles: ESTABLISHMENT_ROLES },
                { name: 'Clientes',      icon: 'customers',  route: paths.dashboard.establishment.customers,  roles: ESTABLISHMENT_ROLES },
            ],
        },
    ];

    const establishmentComingSoonItems = [];

    function shouldShowItem(item) {
        if (item.route && !canOpen(item.route)) return false;
        if (item.roleOnly && user?.role?.id != item.roleOnly) return false;
        if (item.roles && !item.roles.some(r => user?.role?.id == r)) return false;
        return true;
    }

    function shouldShowComingSoon(item) {
        if (isActing) return false;
        return item.roles.some(r => user?.role?.id == r);
    }

    function renderComingSoonItem(item) {
        if (!shouldShowComingSoon(item)) return null;
        const FeatherIcon = featherIcons[item.icon];
        return (
            <NavItemDisabled key={item.name} $collapsed={isSidebarCollapsed} title={isSidebarCollapsed ? item.name : undefined}>
                <NavIconWrapper disabled>
                    {FeatherIcon
                        ? <FeatherIcon size={18} />
                        : (
                            <IconLegacyWrapper>
                                <Icon name={item.icon} color={colors.night + '55'} />
                            </IconLegacyWrapper>
                        )
                    }
                </NavIconWrapper>
                {!isSidebarCollapsed && <NavLabel>{item.name}</NavLabel>}
            </NavItemDisabled>
        );
    }

    const showComingSoonSection = !inEstablishmentPanel && (
        user?.role?.id == roles?.PRODUCER ||
        user?.role?.id == roles?.ESTABLISHMENT ||
        user?.role?.id == roles?.ADMIN
    );

    function renderNavItem(item) {
        if (!shouldShowItem(item)) return null;
        const FeatherIcon = featherIcons[item.icon];

        const isSelected = location.pathname === item.route;

        return (
            <NavItem
                key={item.route}
                to={item.route}
                selected={isSelected}
                $collapsed={isSidebarCollapsed}
                title={isSidebarCollapsed ? item.name : undefined}
            >
                <NavIconWrapper selected={isSelected}>
                    {FeatherIcon
                        ? <FeatherIcon size={18} />
                        : (
                            <IconLegacyWrapper>
                                <Icon
                                    name={item.icon}
                                    color={isSelected ? `var(--panel-accent, ${colors.occasionalPurple})` : colors.night}
                                />
                            </IconLegacyWrapper>
                        )
                    }
                </NavIconWrapper>
                {!isSidebarCollapsed && <NavLabel>{item.name}</NavLabel>}
                {!isSidebarCollapsed && item.isNew && <NewBadge>Novo</NewBadge>}
                {!isSidebarCollapsed && item.icon === 'reservations' && pendingReservationsCount > 0 && (
                    <CountBadge>{pendingReservationsCount}</CountBadge>
                )}
            </NavItem>
        );
    }

    function closeMobileMenu() {
        setIsLateralMenuActive(false);
    }

    return (
        <Container>
            <DashHeader visible />
            <DashHeaderActions />

            {isLateralMenuActive && <MobileOverlay onClick={closeMobileMenu} />}

            <Sidebar active={isLateralMenuActive} $collapsed={isSidebarCollapsed}>
                <SidebarInner>
                    {inEstablishmentPanel ? (
                        <>
                            {!isSidebarCollapsed && <SectionTitle>Estabelecimento</SectionTitle>}
                            {establishmentSections.map((section, si) => (
                                <div key={si}>
                                    {!isSidebarCollapsed && <EstabSectionLabel>{section.label}</EstabSectionLabel>}
                                    <NavSection>
                                        {section.items
                                            .filter(item => shouldShowItem(item))
                                            .map(renderNavItem)
                                        }
                                    </NavSection>
                                </div>
                            ))}
                        </>
                    ) : (
                        <>
                            <NavSection>
                                {!isSidebarCollapsed && <SectionTitle>Principal</SectionTitle>}
                                {mainNavItems.map(renderNavItem)}
                            </NavSection>

                            <NavDivider />

                            <NavSection>
                                {!isSidebarCollapsed && <SectionTitle>Gestão</SectionTitle>}
                                {managementNavItems.map(renderNavItem)}
                            </NavSection>

                            <NavDivider />

                            <NavSection>
                                {!isSidebarCollapsed && <SectionTitle>Ferramentas</SectionTitle>}
                                {toolNavItems.map(renderNavItem)}
                            </NavSection>
                        </>
                    )}

                    {showComingSoonSection && (
                        <>
                            <NavDivider />
                            <NavSection>
                                {!isSidebarCollapsed && (
                                    <SectionTitle>
                                        Em breve
                                        <ComingSoonBadge>
                                            <Clock size={10} />
                                            Novidades
                                        </ComingSoonBadge>
                                    </SectionTitle>
                                )}
                                {(inEstablishmentPanel ? establishmentComingSoonItems : comingSoonItems).map(renderComingSoonItem)}
                            </NavSection>
                        </>
                    )}
                </SidebarInner>
            </Sidebar>

            <SidebarToggle
                type='button'
                onClick={toggleSidebarCollapsed}
                $collapsed={isSidebarCollapsed}
                title={isSidebarCollapsed ? 'Expandir menu' : 'Recolher menu'}
            >
                {isSidebarCollapsed ? <ChevronRight size={14} /> : <ChevronLeft size={14} />}
            </SidebarToggle>

            <MainContent>
                <PanelPermissionToast />
                <Content>
                    {children}
                </Content>
            </MainContent>

            <QuickActionsButton />
        </Container>
    )
}

const Container = styled.div`
    height: auto;
    width: 100%;
    flex-direction: column;

    @media screen and (min-width: ${breakpoints.sm}) {
        display: flex;
        width: 100%;
        flex-direction: row;
    }
`;

const MobileOverlay = styled.div`
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.3);
    z-index: 30;
    backdrop-filter: blur(2px);

    @media screen and (min-width: ${breakpoints.lg}) {
        display: none;
    }
`;

const Sidebar = styled.nav`
    background: #fff;
    width: 0;
    opacity: 0;
    height: 100vh;
    display: flex;
    flex-direction: column;
    padding-top: 100px;
    overflow: hidden;
    position: fixed;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    left: 0;
    top: 0;
    z-index: 31;
    border-right: 1.5px solid ${colors.night}15;

    @media screen and (min-width: ${breakpoints.lg}) {
        opacity: 1 !important;
        width: ${({ $collapsed }) => $collapsed ? '76px' : '240px'};
        padding-top: 110px;
        box-shadow: 0 1px 8px ${colors.night}08;
    }

    ${({ active }) => active && `
        width: 280px;
        opacity: 1;
        box-shadow: 4px 0 24px ${colors.night}12;
    `}
`;

const SidebarToggle = styled.button`
    display: none;
    padding: 0;
    border: 1.5px solid ${colors.night}15;
    background: #fff;
    color: ${colors.night}88;
    cursor: pointer;

    @media screen and (min-width: ${breakpoints.lg}) {
        display: flex;
        align-items: center;
        justify-content: center;
        position: fixed;
        top: 128px;
        left: ${({ $collapsed }) => $collapsed ? '64px' : '228px'};
        width: 24px;
        height: 24px;
        border-radius: 50%;
        box-shadow: 0 1px 4px ${colors.night}15;
        z-index: 32;
        transition: left 0.3s cubic-bezier(0.4, 0, 0.2, 1);

        &:hover {
            background: ${colors.night}08;
            color: ${colors.nightDark};
        }
    }
`;

const SidebarInner = styled.div`
    display: flex;
    flex-direction: column;
    padding: 16px 14px 20px;
    overflow-y: auto;
    flex: 1;

    &::-webkit-scrollbar {
        width: 3px;
    }

    &::-webkit-scrollbar-thumb {
        background: ${colors.night}22;
        border-radius: 10px;
    }
`;

const NavSection = styled.div`
    display: flex;
    flex-direction: column;
    gap: 1px;
`;

const SectionTitle = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 11px;
    font-weight: 600;
    color: ${colors.night}99;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    padding: 0 14px;
    margin-bottom: 4px;
`;

const ComingSoonBadge = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 3px;
    font-size: 9px;
    font-weight: 600;
    color: var(--panel-accent, ${colors.occasionalPurple});
    background: var(--panel-accent-10, ${colors.occasionalPurple}10);
    padding: 2px 6px;
    border-radius: 8px;
    text-transform: uppercase;
    letter-spacing: 0.3px;
`;

const NavDivider = styled.div`
    height: 1px;
    background: ${colors.night}11;
    margin: 10px 14px 8px;
`;

const NavItem = styled(Link).withConfig({
    shouldForwardProp: (prop) => !['selected'].includes(prop),
})`
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 5px 14px;
    border-radius: 10px;
    cursor: pointer;
    text-decoration: none;
    font-size: 14px;
    font-weight: 500;
    color: ${({ selected }) => selected ? `var(--panel-accent, ${colors.occasionalPurple})` : colors.nightDark};
    background: ${({ selected }) => selected ? `var(--panel-accent-0a, ${colors.occasionalPurple}0A)` : 'transparent'};
    transition: all 0.15s ease;
    position: relative;

    ${({ selected }) => selected && `
        font-weight: 600;
        // box-shadow: inset 3px 0 0 var(--panel-accent, ${colors.occasionalPurple});
    `}

    ${({ $collapsed }) => $collapsed && `
        justify-content: center;
        padding: 5px 0;
    `}

    &:hover {
        background: ${({ selected }) => selected ? `var(--panel-accent-0f, ${colors.occasionalPurple}0F)` : colors.night + '08'};
        color: ${({ selected }) => selected ? `var(--panel-accent, ${colors.occasionalPurple})` : colors.nightDark};
    }
`;

const NavItemDisabled = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 9px 14px;
    border-radius: 10px;
    font-size: 14px;
    font-weight: 500;
    color: ${colors.night}55;
    cursor: default;
    opacity: 0.6;

    ${({ $collapsed }) => $collapsed && `
        justify-content: center;
        padding: 9px 0;
    `}
`;

const NavIconWrapper = styled.div.withConfig({
    shouldForwardProp: (prop) => !['selected', 'disabled'].includes(prop),
})`
    width: 30px;
    height: 30px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    background: ${({ selected, disabled }) =>
        disabled ? colors.night + '08'
            : selected ? `var(--panel-accent-15, ${colors.occasionalPurple}15)`
                : colors.night + '08'};
    color: ${({ selected, disabled }) =>
        disabled ? colors.night + '55'
            : selected ? `var(--panel-accent, ${colors.occasionalPurple})`
                : colors.night};
    transition: all 0.15s ease;
`;

const IconLegacyWrapper = styled.div`
    width: 20px;
    height: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
`;

const NavLabel = styled.span`
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const MainContent = styled.main`
    width: 100%;
    padding-top: 120px;
    min-height: 100vh;
    background: ${colors.neutralBackground};

    @media screen and (min-width: ${breakpoints.sm}) {
        min-height: 100vh;
        padding-top: 120px;
    }

    @media screen and (min-width: ${breakpoints.lg}) {
        min-height: 100vh;
    }
`;

const Content = styled.div`
    display: flex;
    border-radius: 8px;
    min-height: calc(100% - 115px);
    padding: 0 20px 20px 20px;
    background: ${colors.neutralBackground};
    justify-content: space-between;
`;

const EstabSectionLabel = styled.div`
    font-size: 10px;
    font-weight: 600;
    color: ${colors.night}66;
    text-transform: uppercase;
    letter-spacing: 0.6px;
    padding: 10px 14px 4px;
    margin-top: 4px;
`;

const NewBadge = styled.span`
    margin-left: auto;
    font-size: 9px;
    font-weight: 600;
    color: #16a34a;
    background: #dcfce7;
    padding: 2px 7px;
    border-radius: 20px;
    text-transform: uppercase;
    letter-spacing: 0.4px;
    flex-shrink: 0;
`;

const CountBadge = styled.span`
    margin-left: auto;
    min-width: 18px;
    height: 18px;
    padding: 0 5px;
    border-radius: 20px;
    background: ${colors.danger};
    color: #fff;
    font-size: 11px;
    font-weight: 700;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
`;
